/**
 * BCR sub-task list component — displays sub-tasks with status indicators.
 * Report links are shown for test tasks: change identification, soc bsw testing, mcu bsw testing.
 */

import React from 'react';
import { Button, Tag, Typography } from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  FileTextOutlined,
  LoadingOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import type { BcrSubTask, BcrTaskStatus } from '@/types';
import { getBcrTaskDisplayName, getBcrReportLabel } from '@/config/bcrTaskConfig';
import { getComparisonReportLabel } from '@/config/comparisonReportLabels';
import { getCompareAreaState, parseSubTaskResult } from './subTaskSemantics';

const { Link } = Typography;

interface BcrSubTaskListProps {
  subTasks: BcrSubTask[];
  taskStatus?: BcrTaskStatus;
  onRerun?: (subTaskName: string) => void;
  isRerunning?: boolean;
  showSocBspReport?: boolean;  // 创建时是否勾选 SOC_BSP：控制是否显示 SOC 底软对比报告链接
}

const statusConfig: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  finished: {
    icon: <CheckCircleOutlined />,
    color: 'success',
    label: '完成',
  },
  running: {
    icon: <LoadingOutlined spin />,
    color: 'processing',
    label: '运行中',
  },
  pending: {
    icon: <ClockCircleOutlined />,
    color: 'default',
    label: '等待',
  },
  stop: {
    icon: <CloseCircleOutlined />,
    color: 'error',
    label: '终止',
  },
};

const BcrSubTaskList: React.FC<BcrSubTaskListProps> = ({ subTasks, taskStatus, onRerun, isRerunning, showSocBspReport = false }) => {
  const canRerunTask = onRerun && (taskStatus === 'finished' || taskStatus === 'stop');

  // Sub-tasks that show an inline report link in the task list.
  // soc/mcu 测试报告已收敛到「测试报告」卡片(BcrReportLinks)展示，这里只保留变更识别行内链接。
  const REPORT_TASK_NAMES = ['change identification'];

  // Internal tasks that should not be displayed to users
  const INTERNAL_TASK_NAMES = ['download artifacts'];

  // Filter out internal tasks from display
  const displayTasks = subTasks.filter(task => !INTERNAL_TASK_NAMES.includes(task.name));

  // 解析 result 字段，支持单个 URL 或 JSON 对象格式
  // fail-semantics:结构化失败标记({failed:true,...})由 parseSubTaskResult 统一识别
  const parseResult = (result: string | null) => {
    console.log(`[parseResult] 开始解析: ${result}`);
    const info = parseSubTaskResult(result);
    if (info.failure) {
      console.log(`[parseResult] 结构化失败标记,跳过 URL 解析`);
      return { isSingle: false };
    }
    if (info.links) {
      console.log(`[parseResult] 多类型格式 → 返回links模式, 键=${Object.keys(info.links)}`);
      return { isSingle: false, links: info.links };
    }
    console.log(`[parseResult] 非多类型JSON → 单URL模式`);
    return { isSingle: info.isSingle, url: info.url };
  };

  // SOC 底软对比：由 soc bsw testing 子任务产出的 bvp /shared 链接（列名沿用 soc_diff_excel，内容是 URL），
  // 展示在「变更识别」行，形态与 MCU变更报告一致的链接。
  // 仅当创建任务时勾选了 SOC_BSP（showSocBspReport，按任务存后端）时才显示。
  const socDiffUrl = subTasks.find(t => t.name === 'soc bsw testing' && t.soc_diff_excel)?.soc_diff_excel ?? null;

  return (
    <div className="text-sm">
      {displayTasks.map((item, index) => {
        const cfg = statusConfig[item.status] || statusConfig.pending;
        const reportLabel = getBcrReportLabel(item.name);
        const hasReport = (item.status === 'finished' || item.status === 'stop') && item.result && REPORT_TASK_NAMES.includes(item.name);
        const showRerun = canRerunTask && (item.status === 'finished' || item.status === 'stop');

        const resultData = hasReport ? parseResult(item.result) : null;
        console.log(`[BcrSubTaskList] 子任务=${item.name}, result=${item.result}, 解析后=`, resultData);
        const isSingleLink = resultData?.isSingle;
        // 「变更识别」行同时展示两份独立报告：
        //   multiLinks   = 包文件对比（含 SOC_BSP 镜像扫描），来自 sub_task.result dict
        //   socDiffUrl   = SOC 测试采集的底软横向对比，来自 soc_diff_excel
        const links = resultData?.links;
        const multiLinks = links && Object.keys(links).length > 0 ? links : undefined;
        // SOC 底软对比链接显示在「变更识别」行（SOC_BSP 那一格），仅勾选了 SOC_BSP 时显示
        const showSocDiffLink = item.name === 'change identification' && !!socDiffUrl && showSocBspReport;

        // fail-semantics(2026-08-26):三态语义——失败/上游失败未执行/成功无变化/成功有变化
        const areaState = getCompareAreaState(item, subTasks);

        return (
          <div key={item.name} className="flex items-center justify-between w-full py-1 border-b border-gray-100 last:border-0">
            <div className="flex items-center gap-2">
              <span className="text-gray-400 w-5 text-right">
                {index + 1}.
              </span>
              <span>{getBcrTaskDisplayName(item.name)}</span>
              {hasReport && isSingleLink && resultData.url?.startsWith('http') && (
                <Link
                  href={resultData.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-2"
                >
                  <FileTextOutlined className="mr-1" />
                  {reportLabel}
                </Link>
              )}
              {hasReport && multiLinks && (
                <div className="ml-2 flex items-center gap-1">
                  {Object.entries(multiLinks).map(([type, url]) => (
                    typeof url === 'string' && url.startsWith('http') && (
                      <Link
                        key={type}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs"
                      >
                        <FileTextOutlined className="mr-1" />
                        {getComparisonReportLabel(type)}
                      </Link>
                    )
                  ))}
                </div>
              )}
              {/* SOC 底软对比：SOC 测试产出的 bvp /shared 链接，显示在变更识别行 */}
              {showSocDiffLink && (
                <Link
                  href={socDiffUrl!}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-2"
                >
                  <FileTextOutlined className="mr-1" />
                  SOC底软对比报告
                </Link>
              )}
              {/* fail-semantics:对比区三态——失败(红)/上游失败未执行(橙)/成功无变化(灰)/成功有变化(绿) */}
              {areaState.kind === 'failure' && (
                <span className="ml-2 text-red-500 text-xs">
                  对比失败: {areaState.reason}
                  {areaState.failedStep && `（${getBcrTaskDisplayName(areaState.failedStep) || areaState.failedStep}）`}
                </span>
              )}
              {areaState.kind === 'upstream-stopped' && (
                <span className="ml-2 text-orange-500 text-xs">
                  因上游「{getBcrTaskDisplayName(areaState.stepName) || areaState.stepName}」失败，对比未执行
                </span>
              )}
              {areaState.kind === 'success-empty' && (
                <span className="ml-2 text-gray-400 text-xs">对比成功，无接口变化</span>
              )}
              {areaState.kind === 'success-changes' && (
                <span className="ml-2 text-green-600 text-xs">{areaState.moduleCount} 处接口变化</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {showRerun && (
                <Button
                  type="link"
                  size="small"
                  icon={<RedoOutlined />}
                  loading={isRerunning}
                  onClick={() => onRerun!(item.name)}
                >
                  重跑
                </Button>
              )}
              <div className="flex items-center gap-1">
                <Tag icon={cfg.icon} color={cfg.color}>
                  {cfg.label}
                </Tag>
                {item.status === 'finished' && item.duration_seconds !== null && (
                  <span className="text-gray-500 text-xs">
                    {item.duration_seconds}s
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default BcrSubTaskList;
